import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { ArrowClockwise, EnvelopePlus, PlayBtn } from "react-bootstrap-icons";
import DatePicker from "react-datepicker";
import moment from "moment";

import Sidebar from "../../components/admin/sidebar/Sidebar";
import Footer from "../../components/admin/footer/Footer";
import Loader from "../../components/common/Loader";
import NetFeeBulletin from "../../components/bulletin/NetFeeBulletin";
import InformationTooltip from "../../components/common/tooltipInformation/InformationTooltip";
import { informationEnBn } from "../../components/common/tooltipInformation/informationEnBn";
import { smsCount } from "../../components/common/UtilityMethods";
import { FontColor, FourGround } from "../../assets/js/theme";
import apiLink from "../../api/apiLink";
import { getArea } from "../../features/apiCalls";
import { getSubAreasApi } from "../../features/actions/customerApiCall";
import {
  audioFileUpload,
  getBulletinPermission,
} from "../../features/apiCallAdmin";
import { getPersonalMessageGatewayBalance } from "../../features/messageLogApi";
import { areasSubareasChecked } from "../staff/staffCustomFucn";
import useISPowner from "../../hooks/useISPOwner";
import SMSPurchase from "./SMSPurchase";
import MessageAlert from "./MessageAlert";
import PlayTutorial from "../tutorial/PlayTutorial";
import AudioUploadPlayer from "../settings/template/AudioUploadPlayer";

import "./message.css";
import useDash from "../../assets/css/dash.module.css";

/* ------------------------------ Config ------------------------------ */

const MAX_SMS_LENGTH = 480;
const VOICE_SECONDS_PER_CREDIT = 30;

const BALANCE_FIELD = {
  nonMasking: "smsBalance",
  masking: "maskingSmsBalance",
  fixedNumber: "fixedNumberSmsBalance",
  voice: "voiceMessageBalance",
};

const TEMPLATE_ITEMS = [
  "USER: USERNAME",
  "ID: CUSTOMER_ID",
  "NAME: CUSTOMER_NAME",
  "BILL: AMOUNT Tk",
  "LAST DATE: BILL_DATE",
  "BILL DUE: BILL_DUE",
];

// Only shown when the ISP owner has a payment gateway (bpSettings.hasPG).
// Works like every other token, so it sits wherever the user selects it.
const PAYMENT_LINK_ITEM = "Payment Link: PAYMENT_LINK";

const DAY_OPTIONS = [
  { value: 1, labelKey: "billDueOneDay" },
  { value: 2, labelKey: "billDueTwoDay" },
  { value: 3, labelKey: "billDueThreeDay" },
  { value: 4, labelKey: "billDueFourDay" },
  { value: 5, labelKey: "billDueFiveDay" },
  { value: 6, labelKey: "billDueSixDay" },
  { value: 7, labelKey: "billDueSevenDay" },
];

const RECEIVER_TYPES = [
  { value: "unpaidCustomerByDate", labelKey: "billDueExpire" },
  { value: "allCustomer", labelKey: "sokolCustomer" },
  { value: "paid", labelKey: "payPaid" },
  { value: "unpaid", labelKey: "unpaid" },
  { value: "active", labelKey: "active", hasTooltip: true },
  { value: "inactive", labelKey: "in active" },
  { value: "expired", labelKey: "expired" },
  { value: "billDate", labelKey: "billDate" },
];

// Each receiver type decides which customers get the message.
const RECEIVER_FILTERS = {
  unpaidCustomerByDate: (c, { days, now }) =>
    c.monthlyFee > c.balance &&
    days.includes(moment(c.billingCycle).diff(now, "days")),
  allCustomer: () => true,
  paid: (c) => c.paymentStatus === "paid",
  unpaid: (c) => c.paymentStatus === "unpaid",
  active: (c, { customerTypes }) =>
    c.status === "active" && customerTypes.includes(c.paymentStatus),
  inactive: (c) => c.status === "inactive",
  expired: (c) => c.status === "expired",
  billDate: (c, { billDate }) =>
    c.monthlyFee > c.balance &&
    moment(c.billingCycle).date() === billDate.getDate(),
};

/* ------------------------------ Helpers ------------------------------ */

const toggleInList = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

const getUsername = (c) =>
  ({
    pppoe: c.pppoe?.name,
    static: c.queue?.target?.split("/")[0],
  })[c.userType] || "";

// Single-pass replace, so a customer's data can never be re-replaced as a token.
const TOKEN_REGEX =
  /CUSTOMER_NAME|CUSTOMER_ID|BILL_DATE|BILL_DUE|AMOUNT|USERNAME|PAYMENT_LINK/g;

const makeTextItem = (template, ispOwnerId, customer, paymentUrl) => {
  const due = Math.max(customer.monthlyFee - customer.balance, 0);
  const values = {
    CUSTOMER_NAME: customer.name || "",
    CUSTOMER_ID: customer.customerId || "",
    BILL_DATE: moment(customer.billingCycle).format("DD-MM-YYYY hh:mm A"),
    BILL_DUE: due,
    AMOUNT: customer.monthlyFee || "",
    USERNAME: getUsername(customer),
    PAYMENT_LINK: paymentUrl,
  };
  const message = template.replace(TOKEN_REGEX, (token) => values[token]);

  return {
    app: "onebilling",
    type: "bulk",
    senderId: ispOwnerId,
    message,
    mobile: customer.mobile,
    count: smsCount(message.trim()),
  };
};

const getAudioDuration = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const el = new Audio(url);
    el.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      resolve(el.duration);
    };
    el.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Invalid audio file"));
    };
  });

const hasEnoughBalance = (owner, sendBy, total) => {
  const field = BALANCE_FIELD[sendBy];
  return !field || owner[field] >= total;
};

const RadioGroup = ({
  title,
  name,
  options,
  value,
  onChange,
  t,
  as: Heading = "h5",
}) => (
  <div className="message-sending-type">
    <Heading>{title}</Heading>
    <div className="smsType">
      {options.map((opt) => (
        <div className="message_radio" key={opt.value}>
          <input
            type="radio"
            id={`${name}-${opt.value}`}
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={(e) => onChange(e.target.value)}
          />
          <label htmlFor={`${name}-${opt.value}`}>{opt.label}</label>
        </div>
      ))}
    </div>
  </div>
);

const AreaSelector = ({ area, areaSubareas, onChange, t }) => (
  <>
    <div style={{ width: "200px", height: "30px" }}>
      <input
        type="checkbox"
        id="allAreas"
        name="allAreas"
        className="form-check-input"
        onChange={onChange}
        checked={
          !!areaSubareas?.length && areaSubareas.every((s) => s.isChecked)
        }
      />
      <label htmlFor="allAreas" className="areaParent ms-1">
        {t("allArea")}
      </label>
    </div>

    <div className="AllAreaClass mb-4">
      {area?.map((val) => (
        <div key={val.id}>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              name="area"
              id={val.id}
              onChange={onChange}
              checked={
                !!areaSubareas && areasSubareasChecked(val.id, areaSubareas)
              }
            />
            <label htmlFor={val.id} className="areaParent ms-1">
              {val.name}
            </label>
          </div>

          {areaSubareas
            ?.filter((sub) => sub.area === val.id)
            .map((sub) => (
              <div key={sub.id} className="displayFlex">
                <input
                  type="checkbox"
                  id={sub.id}
                  onChange={onChange}
                  checked={sub.isChecked}
                />
                <label htmlFor={sub.id} className="text-secondary">
                  {sub.name}
                </label>
              </div>
            ))}
        </div>
      ))}
    </div>
  </>
);

const ReceiverSelector = ({
  value,
  onChange,
  days,
  onToggleDay,
  customerTypes,
  onToggleCustomerType,
  billDate,
  onBillDateChange,
  t,
}) => (
  <div className="displayGrid radio-buttons">
    {RECEIVER_TYPES.map(({ value: type, labelKey, hasTooltip }) => (
      <div key={type}>
        <div className="d-flex align-items-center">
          <input
            id={`receiver-${type}`}
            type="radio"
            name="platform"
            value={type}
            className="form-check-input"
            checked={value === type}
            onChange={(e) => onChange(e.target.value)}
          />
          <label className="form-check-lebel ms-2" htmlFor={`receiver-${type}`}>
            {t(labelKey)}
          </label>

          {hasTooltip && informationEnBn()?.[0] && (
            <InformationTooltip data={informationEnBn()[0]} />
          )}
        </div>

        {type === "unpaidCustomerByDate" && value === type && (
          <div className="displayFlex">
            {DAY_OPTIONS.map((day) => (
              <React.Fragment key={day.value}>
                <input
                  type="checkbox"
                  checked={days.includes(day.value)}
                  onChange={() => onToggleDay(day.value)}
                />
                <label className="mx-3">{t(day.labelKey)}</label>
              </React.Fragment>
            ))}
          </div>
        )}

        {type === "active" && value === type && (
          <div className="ms-4">
            {[
              { id: "activePaid", value: "paid", labelKey: "payPaid" },
              { id: "activeUnpaid", value: "unpaid", labelKey: "unpaid" },
            ].map((opt) => (
              <div key={opt.id}>
                <input
                  type="checkbox"
                  id={opt.id}
                  className="form-check-input"
                  checked={customerTypes.includes(opt.value)}
                  onChange={() => onToggleCustomerType(opt.value)}
                />
                <label className="form-check-lebel ms-2" htmlFor={opt.id}>
                  {t(opt.labelKey)}
                </label>
              </div>
            ))}
          </div>
        )}

        {type === "billDate" && value === type && (
          <DatePicker
            className="form-control mt-1 mw-100"
            selected={billDate}
            onChange={onBillDateChange}
            dateFormat="dd/MM/yyyy"
            minDate={billDate}
          />
        )}
      </div>
    ))}
  </div>
);

export default function Message() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { role, ispOwnerData, ispOwnerId, bpSettings } = useISPowner();

  const area = useSelector((state) => state.area.area);
  const storeSubArea = useSelector((state) => state.area?.subArea);
  const maskingId = useSelector(
    (state) => state.persistedReducer.auth.currentUser.ispOwner?.maskingId,
  );
  const butPermission = useSelector(
    (state) => state.adminNetFeeSupport?.bulletinPermission,
  );
  const gatewayBalance = useSelector(
    (state) => state?.messageLog?.gatewayBalance,
  );

  // balances & ui
  const [balances, setBalances] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [show, setShow] = useState(false);
  const [modalStatus, setModalStatus] = useState("");

  // message settings
  const [messageType, setMessageType] = useState("text");
  const [sendingType, setSendingType] = useState("nonMasking");
  const [audio, setAudio] = useState(null);

  // text message content
  const [title, setTitle] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [bottomText, setBottomText] = useState("");

  // receivers
  const [areaSubareas, setAreaSubareas] = useState([]);
  const [receiverType, setReceiverType] = useState("");
  const [days, setDays] = useState([]);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [billDate, setBillDate] = useState(new Date());

  const isVoice = messageType === "voice";
  const hasPG = !!ispOwnerData?.bpSettings?.hasPG;
  const paymentUrl = `https://app.one-billing.com/isp/${ispOwnerData?.netFeeId}`;

  /* ---- derived values ---- */

  const templateItems = useMemo(
    () => (hasPG ? [...TEMPLATE_ITEMS, PAYMENT_LINK_ITEM] : TEMPLATE_ITEMS),
    [hasPG],
  );

  const upperText = useMemo(
    () => selectedItems.map((i) => `\n${i}`).join(""),
    [selectedItems],
  );
  const messageTemplate = `${title}${upperText}\n${bottomText}`;

  const expand = useCallback(
    (text) => text.replace(/PAYMENT_LINK/g, paymentUrl),
    [paymentUrl],
  );
  const previewMessage = expand(messageTemplate);

  const messageTypes = useMemo(
    () => [
      { label: t("text"), value: "text" },
      ...(bpSettings?.voiceMessage
        ? [{ label: t("voice"), value: "voice" }]
        : []),
    ],
    [t, bpSettings?.voiceMessage],
  );

  const sendingMessageTypes = useMemo(
    () => [
      { label: t("nonMasking"), value: "nonMasking" },
      { label: t("masking"), value: "masking" },
      { label: t("fixedNumber"), value: "fixedNumber" },
      ...(bpSettings?.hasSmsGateway
        ? [{ label: t("personalGateway"), value: "personalGateway" }]
        : []),
    ],
    [t, bpSettings?.hasSmsGateway],
  );

  const balanceItems = [
    { key: "nonMasking", value: balances.smsBalance },
    { key: "masking", value: balances.maskingSmsBalance },
    { key: "fixedNumber", value: balances.fixedNumberSmsBalance },
    ...(bpSettings?.voiceMessage
      ? [{ key: "voice", value: balances.voiceMessageBalance }]
      : []),
    ...(bpSettings?.hasSmsGateway
      ? [{ key: "personalGateway", value: gatewayBalance }]
      : []),
  ];

  /* ---- data loading ---- */

  const fetchBalances = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const res = await apiLink.get(`/ispOwner/${ispOwnerId}`);
      setBalances(res.data);
    } catch (error) {
      console.log(error.response?.data.message);
    } finally {
      setIsRefreshing(false);
    }
  }, [ispOwnerId]);

  useEffect(() => {
    if (role === "ispOwner" || role === "manager") {
      fetchBalances();
      if (area.length === 0) getArea(dispatch, ispOwnerId, () => {});
      getSubAreasApi(dispatch, ispOwnerId);
    }

    if (bpSettings?.hasSmsGateway)
      getPersonalMessageGatewayBalance(dispatch, () => {});

    if (Object.keys(butPermission || {}).length === 0)
      getBulletinPermission(dispatch);
  }, [role, fetchBalances]);

  useEffect(() => {
    const subAreas = (area || []).flatMap((a) =>
      (storeSubArea || [])
        .filter((sub) => sub.area === a.id)
        .map((sub) => ({ ...sub, isChecked: false })),
    );
    setAreaSubareas(subAreas);
  }, [area, storeSubArea]);

  /* ---- handlers ---- */

  const handleAreaChange = ({ target: { name, checked, id } }) => {
    setAreaSubareas((prev) =>
      prev.map((sub) => {
        if (name === "allAreas") return { ...sub, isChecked: checked };
        if (name === "area")
          return sub.area === id ? { ...sub, isChecked: checked } : sub;
        return sub.id === id ? { ...sub, isChecked: checked } : sub;
      }),
    );
  };

  const handleToggleItem = (item) => {
    if (selectedItems.includes(item)) {
      setSelectedItems((prev) => prev.filter((i) => i !== item));
      return;
    }
    if (previewMessage.length + expand(item).length > MAX_SMS_LENGTH) {
      toast.error(t("exceedSMSLimit"));
      return;
    }
    setSelectedItems((prev) => [...prev, item]);
  };

  const resetForm = () => {
    setAreaSubareas((prev) =>
      prev.map((sub) => ({ ...sub, isChecked: false })),
    );
    setDays([]);
    setCustomerTypes([]);
    setSelectedItems([]);
    setTitle("");
    setBottomText("");
    setAudio(null);
  };

  const getReceivers = (customers) => {
    const subAreaIds = new Set(
      areaSubareas.filter((s) => s.isChecked).map((s) => s.id),
    );
    const filter = RECEIVER_FILTERS[receiverType];
    if (!filter) return [];

    const ctx = { days, customerTypes, billDate, now: moment() };

    return customers.filter(
      (c) => c.mobile && subAreaIds.has(c.subArea) && filter(c, ctx),
    );
  };

  const sendTextMessage = async (owner, receivers) => {
    const items = receivers.map((c) =>
      makeTextItem(messageTemplate, ispOwnerId, c, paymentUrl),
    );
    const totalSmsCount = items.reduce((sum, item) => sum + item.count, 0);

    if (sendingType === "masking" && !maskingId) {
      toast.error(t("maskingIdNotFound"));
      return;
    }

    alert(` ${t("sampleSMS")}:\n${items[0].message}`);

    if (!hasEnoughBalance(owner, sendingType, totalSmsCount)) {
      toast.error(t("unseccessAlertSMS"));
      return;
    }

    if (
      !window.confirm(
        `${items.length}  ${t("getSMS")} ${totalSmsCount}  ${t("expenseSMS")}`,
      )
    )
      return;

    return apiLink.post(`sms/bulk/${ispOwnerId}`, {
      items,
      totalSmsCount,
      sendBy: sendingType,
    });
  };

  const sendVoiceMessage = async (owner, receivers) => {
    const numbers = receivers.map((c) => c.mobile);

    const duration = await getAudioDuration(audio);
    const creditPerCall = Math.max(
      1,
      Math.ceil(duration / VOICE_SECONDS_PER_CREDIT),
    );
    const totalSmsCount = numbers.length * creditPerCall;

    if (!hasEnoughBalance(owner, "voice", totalSmsCount)) {
      toast.error(t("unseccessAlertSMS"));
      return;
    }

    if (
      !window.confirm(
        `${numbers.length}  ${t("getSMS")} ${totalSmsCount}  ${t("expenseSMS")}`,
      )
    )
      return;

    const formData = new FormData();
    formData.append("file", audio);

    const uploaded = await audioFileUpload(() => {}, formData);
    const uploadedFile = uploaded?.[0];
    if (!uploadedFile) {
      toast.error("Voice file upload failed.");
      return;
    }

    return apiLink.post(`sms/bulk/${ispOwnerId}`, {
      messageType: "voice",
      numbers,
      file: {
        originalName: uploadedFile.originalName,
        path: uploadedFile.path,
        publicID: uploadedFile.publicID,
        duration: uploadedFile.duration,
      },
      totalSmsCount,
    });
  };

  const handleSendMessage = async () => {
    if (isVoice && !audio) {
      toast.error("Voice file is required.");
      return;
    }

    setIsSending(true);
    try {
      const [{ data: owner }, { data: customers }] = await Promise.all([
        apiLink.get(`/ispOwner/${ispOwnerId}`),
        apiLink.get(`/ispOwner/all-customer/${ispOwnerId}`),
      ]);

      const receivers = getReceivers(customers);
      if (receivers.length === 0) {
        alert(t("notFoundCustomer"));
        return;
      }

      const res = isVoice
        ? await sendVoiceMessage(owner, receivers)
        : await sendTextMessage(owner, receivers);

      if (res?.data?.status) {
        toast.success(t("successSMS"));
        resetForm();
        fetchBalances();
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response?.data?.message || error.message);
    } finally {
      setIsSending(false);
    }
  };

  const openModal = (status) => {
    setModalStatus(status);
    setShow(true);
  };

  return (
    <>
      <Sidebar />
      <ToastContainer position="top-right" theme="colored" />
      <div className={useDash.dashboardWrapper}>
        <div className="container-fluied collector">
          <div className="container">
            <FontColor>
              <FourGround>
                <div className="collectorTitle d-flex justify-content-between px-4">
                  <div>{t("SMSboard")}</div>

                  <div className="d-flex align-items-center">
                    <div className="addAndSettingIcon">
                      <PlayBtn
                        className="addcutmButton"
                        onClick={() => openModal("playTutorial")}
                        title={t("tutorial")}
                      />
                    </div>

                    <div
                      className="textButton"
                      onClick={() => openModal("buySms")}
                    >
                      <EnvelopePlus className="text_icons" /> {t("buySms")}
                    </div>
                  </div>
                </div>
              </FourGround>

              <FourGround>
                <div className="collectorWrapper">
                  <div className="profileWrapper uiChange">
                    <div className="clintTitle align-items-center p-2">
                      {balanceItems.map(({ key, value }) => (
                        <div key={key}>
                          <label className="form-control-label fw-bold">
                            {t(key)}
                          </label>
                          <span
                            className={`text-${value >= 0 ? "success" : "danger"} fw-bold`}
                          >
                            {value}
                          </span>
                        </div>
                      ))}

                      <div title={t("refresh")} className="btn btn-primary">
                        {isRefreshing ? (
                          <Loader />
                        ) : (
                          <ArrowClockwise onClick={fetchBalances} />
                        )}
                      </div>
                    </div>

                    <div className="messageStatus d-flex justify-content-between">
                      <RadioGroup
                        title={t("messageType")}
                        name="messageType"
                        options={messageTypes}
                        value={messageType}
                        onChange={setMessageType}
                        t={t}
                      />

                      {!isVoice && (
                        <RadioGroup
                          title={t("sendingMessageType")}
                          name="sendingType"
                          as="h4"
                          options={sendingMessageTypes}
                          value={sendingType}
                          onChange={setSendingType}
                          t={t}
                        />
                      )}
                    </div>

                    <div className="writeMessageSection">
                      <div className="ifNotCheckBox">
                        <AreaSelector
                          area={area}
                          areaSubareas={areaSubareas}
                          onChange={handleAreaChange}
                          t={t}
                        />

                        <div className="d-flex justify-content-between align-items-center">
                          <ReceiverSelector
                            value={receiverType}
                            onChange={setReceiverType}
                            days={days}
                            onToggleDay={(day) =>
                              setDays((prev) => toggleInList(prev, day))
                            }
                            customerTypes={customerTypes}
                            onToggleCustomerType={(type) =>
                              setCustomerTypes((prev) =>
                                toggleInList(prev, type),
                              )
                            }
                            billDate={billDate}
                            onBillDateChange={setBillDate}
                            t={t}
                          />

                          {isVoice ? (
                            <AudioUploadPlayer
                              file={audio}
                              savedAudio={{}}
                              onFileChange={setAudio}
                            />
                          ) : (
                            <div>
                              <div className="mt-3">
                                <input
                                  value={title}
                                  onChange={(e) => setTitle(e.target.value)}
                                  className="form-control"
                                  type="text"
                                  placeholder={t("title")}
                                  maxLength={40}
                                />
                              </div>

                              {templateItems.map((item, index) => (
                                <div className="radioselect" key={item}>
                                  <input
                                    id={`template-${index}`}
                                    type="checkbox"
                                    checked={selectedItems.includes(item)}
                                    onChange={() => handleToggleItem(item)}
                                  />
                                  <label
                                    className="templatelabel"
                                    htmlFor={`template-${index}`}
                                  >
                                    {item}
                                  </label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      <br />

                      {!isVoice && (
                        <>
                          <div className="showthesequence">
                            <p className="endingText">{title}</p>
                            {selectedItems.map((item) => (
                              <p key={item}>{item}</p>
                            ))}
                            <p className="endingtext">{bottomText}</p>
                          </div>

                          <div className="smsCount">
                            <span className="smsLength">
                              {t("letter")}
                              {previewMessage.length}
                            </span>
                            <span>SMS:{smsCount(previewMessage)}</span>
                          </div>

                          <textarea
                            id="messageTextArea"
                            rows="6"
                            className="form-control mt-4"
                            placeholder={t("messageLikhun")}
                            value={bottomText}
                            maxLength={Math.max(
                              0,
                              335 - expand(upperText).length,
                            )}
                            onChange={(e) => setBottomText(e.target.value)}
                          />
                        </>
                      )}

                      <hr />
                      <button
                        onClick={handleSendMessage}
                        className="btn btn-success"
                        disabled={isSending}
                      >
                        {isSending ? <Loader /> : t("sendMessage")}
                      </button>
                    </div>
                  </div>
                </div>

                {(butPermission?.message || butPermission?.allPage) && (
                  <NetFeeBulletin />
                )}
              </FourGround>
              <Footer />
            </FontColor>
          </div>
        </div>
      </div>

      <MessageAlert ispOwner={ispOwnerData} />

      {modalStatus === "buySms" && (
        <SMSPurchase show={show} setShow={setShow} />
      )}

      {modalStatus === "playTutorial" && (
        <PlayTutorial {...{ show, setShow, video: "smsTemplate" }} />
      )}
    </>
  );
}
